import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { API_CONFIG } from '../../config';
import { ICONS } from '../../constants';

interface DefaultCommissionsPanelProps {
  canEdit: boolean;
}

interface CommissionSettings {
  default_producer_percentage: string;
  default_producer_fixed_fee: string;
  default_use_producer_fixed_fee: boolean;
  default_coordinator_fee: string;
  default_coordinator_percentage: string;
  default_use_coordinator_fixed_fee: boolean;
  default_supervisor_fee: string;
  default_supervisor_percentage: string;
  default_use_supervisor_fixed_fee: boolean;
  default_finder_fee: string;
  default_finder_percentage: string;
  default_use_finder_fixed_fee: boolean;
}

const DefaultCommissionsPanel: React.FC<DefaultCommissionsPanelProps> = ({ canEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<CommissionSettings>({
    default_producer_percentage: '10',
    default_producer_fixed_fee: '0',
    default_use_producer_fixed_fee: false,
    default_coordinator_fee: '0',
    default_coordinator_percentage: '5',
    default_use_coordinator_fixed_fee: true,
    default_supervisor_fee: '0',
    default_supervisor_percentage: '5',
    default_use_supervisor_fixed_fee: true,
    default_finder_fee: '0',
    default_finder_percentage: '3',
    default_use_finder_fixed_fee: true,
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/defaults`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      // Convert numbers to strings for controlled inputs
      setSettings({
        default_producer_percentage: String(data.default_producer_percentage ?? 10),
        default_producer_fixed_fee: String(data.default_producer_fixed_fee ?? 0),
        default_use_producer_fixed_fee: data.default_use_producer_fixed_fee ?? false,
        default_coordinator_fee: String(data.default_coordinator_fee ?? 0),
        default_coordinator_percentage: String(data.default_coordinator_percentage ?? 5),
        default_use_coordinator_fixed_fee: data.default_use_coordinator_fixed_fee ?? true,
        default_supervisor_fee: String(data.default_supervisor_fee ?? 0),
        default_supervisor_percentage: String(data.default_supervisor_percentage ?? 5),
        default_use_supervisor_fixed_fee: data.default_use_supervisor_fixed_fee ?? true,
        default_finder_fee: String(data.default_finder_fee ?? 0),
        default_finder_percentage: String(data.default_finder_percentage ?? 3),
        default_use_finder_fixed_fee: data.default_use_finder_fixed_fee ?? true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: keyof CommissionSettings, value: string | boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Convert strings back to numbers for API
      const payload = {
        default_producer_percentage: parseFloat(settings.default_producer_percentage) || 0,
        default_producer_fixed_fee: parseFloat(settings.default_producer_fixed_fee) || 0,
        default_use_producer_fixed_fee: settings.default_use_producer_fixed_fee,
        default_coordinator_fee: parseFloat(settings.default_coordinator_fee) || 0,
        default_coordinator_percentage: parseFloat(settings.default_coordinator_percentage) || 0,
        default_use_coordinator_fixed_fee: settings.default_use_coordinator_fixed_fee,
        default_supervisor_fee: parseFloat(settings.default_supervisor_fee) || 0,
        default_supervisor_percentage: parseFloat(settings.default_supervisor_percentage) || 0,
        default_use_supervisor_fixed_fee: settings.default_use_supervisor_fixed_fee,
        default_finder_fee: parseFloat(settings.default_finder_fee) || 0,
        default_finder_percentage: parseFloat(settings.default_finder_percentage) || 0,
        default_use_finder_fixed_fee: settings.default_use_finder_fixed_fee,
      };

      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/defaults`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }

      setSuccess(t('rates.commissions.saveSuccess') || 'Комиссии сохранены');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const CommissionRow: React.FC<{
    label: string;
    percentageKey: keyof CommissionSettings;
    fixedKey: keyof CommissionSettings;
    useFixedKey: keyof CommissionSettings;
  }> = ({ label, percentageKey, fixedKey, useFixedKey }) => {
    const useFixed = settings[useFixedKey] as boolean;
    
    return (
      <div className="bg-gray-700/30 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white font-medium">{label}</span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleChange(useFixedKey, false)}
              disabled={!canEdit}
              className={`
                px-3 py-1.5 text-xs rounded-l-lg border transition-colors
                ${!useFixed
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {t('rates.commissions.percentage') || '%'}
            </button>
            <button
              onClick={() => handleChange(useFixedKey, true)}
              disabled={!canEdit}
              className={`
                px-3 py-1.5 text-xs rounded-r-lg border transition-colors
                ${useFixed
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {t('rates.commissions.fixed') || 'Фикс'}
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Percentage input */}
          <div className={`flex-1 ${useFixed ? 'opacity-40' : ''}`}>
            <label className="text-xs text-gray-400 mb-1 block">
              {t('rates.commissions.percentage') || 'Процент'} (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings[percentageKey] as string}
              onChange={(e) => handleChange(percentageKey, e.target.value)}
              disabled={!canEdit || useFixed}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white
                focus:outline-none focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          
          {/* Fixed input */}
          <div className={`flex-1 ${!useFixed ? 'opacity-40' : ''}`}>
            <label className="text-xs text-gray-400 mb-1 block">
              {t('rates.commissions.fixed') || 'Фикс'} (руб)
            </label>
            <input
              type="number"
              min="0"
              step="100"
              value={settings[fixedKey] as string}
              onChange={(e) => handleChange(fixedKey, e.target.value)}
              disabled={!canEdit || !useFixed}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white
                focus:outline-none focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        <span className="ml-3 text-gray-400">{t('rates.common.loading') || 'Загрузка...'}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('rates.commissions.title') || 'Комиссии по умолчанию'}
        </h3>
        <p className="text-gray-400 text-sm">
          {t('rates.commissions.description') || 'Значения по умолчанию для комиссий в новых сметах'}
        </p>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Not editable warning */}
      {!canEdit && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400 text-sm">
          {t('rates.common.noPermission') || 'Недостаточно прав для редактирования'}
        </div>
      )}

      {/* Commission rows */}
      <CommissionRow
        label={t('rates.commissions.producer') || 'Продюсер'}
        percentageKey="default_producer_percentage"
        fixedKey="default_producer_fixed_fee"
        useFixedKey="default_use_producer_fixed_fee"
      />
      
      <CommissionRow
        label={t('rates.commissions.coordinator') || 'Координатор'}
        percentageKey="default_coordinator_percentage"
        fixedKey="default_coordinator_fee"
        useFixedKey="default_use_coordinator_fixed_fee"
      />
      
      <CommissionRow
        label={t('rates.commissions.supervisor') || 'Супервайзер'}
        percentageKey="default_supervisor_percentage"
        fixedKey="default_supervisor_fee"
        useFixedKey="default_use_supervisor_fixed_fee"
      />
      
      <CommissionRow
        label={t('rates.commissions.finder') || 'Finder'}
        percentageKey="default_finder_percentage"
        fixedKey="default_finder_fee"
        useFixedKey="default_use_finder_fixed_fee"
      />

      {/* Save button */}
      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`
              flex items-center space-x-2 px-6 py-2.5 rounded-lg font-medium transition-all
              ${hasChanges
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }
              disabled:opacity-50
            `}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('rates.common.loading') || 'Сохранение...'}</span>
              </>
            ) : (
              <>
                <ICONS.Edit className="w-4 h-4" />
                <span>{t('rates.common.save') || 'Сохранить'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default DefaultCommissionsPanel;
